import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { SectionFieldModel } from '../models/section-field.model';
import { SectionFieldType } from '../enum/section-field.type';
import { ModalsTypeEnum } from '../enum/modals-type.enum';
import { CoreService } from '../../core/services/core.service';
import { CaseConfigData, CaseConfigField, CaseConfigUserDefineField } from '../models/civil-case.model';
import { UserDefineField as UserDefineFieldData } from '../models/models';
import { commonFormatter, commonTildFormatter, RetriveCodeDescriptionUsingTilde, RetriveDescription } from '../utils/functions';
import { ValidationMethod } from '../component/typeahead/typeahead.service';
import { UserDefineFieldType } from '../enum/user-define-field.type';
import { CivilCaseTabType } from '../enum/civil-case-tab.type';
import { CaseConfigTab, CaseConfigTabModel } from '../models/case-config-tab.model';

@Injectable({
  providedIn: 'root',
})
export class TemplateMapService {
  constructor(
    private coreService: CoreService
  ) { }

  mapUserDefineField(
    field: CaseConfigUserDefineField,
    data: UserDefineFieldData[]
  ): SectionFieldModel {
    const result = <SectionFieldModel>{
      id: field.id,
      order: field.order,
      label: field.label,
      value: data.find(udField => udField.name == field.label)?.value,
      sectionId: field.sectionId,
      formControlName: `${field.label}-${field.id}`,
      isDisabled: field.isDisabled ?? false,
      width: field.width === 0 ? 100 : field.width,
      type: SectionFieldType.LabelInputControl,
      modalType: null,
      updateOn: 'change',
      errorMessage: '',
      items$: null,
      tooltip: '',
      modalValue: null,
      hasData: null,
      shortcut: '',
      textAlign: 'left',
    };

    switch (field.type) {
      case UserDefineFieldType.Date:
        result.type = SectionFieldType.DateInputControl;
        break;
      case UserDefineFieldType.Input:
        result.type = SectionFieldType.LabelInputControl;
        break;
      case UserDefineFieldType.Textarea:
        result.type = SectionFieldType.TextareaControl;
        break;
      case UserDefineFieldType.Dropdown:
        result.type = SectionFieldType.DropdownControl;
        result.modalType = ModalsTypeEnum.PromptChoice;
        result.dropdownMethodName = "SearchUserDefinedOption";
        result.dropdownContextKey = field.id.toString();
        result.dropdownValidationConfig = {
          serviceName: "Validation.asmx",
          methodName: ValidationMethod.IsTableHasRecord,
          strTableName: "{{Enum500}}",
          strWhere: "promptChoice='{inputValue}' and UDFSequenceNumber=" + field.id,
          errorMessage: `Invalid option`,
          bUseQuote: false,
          bUseQuoteForWhere: false,
          strColumnName: '',
          strWhereColumnName: '',
          strWhereColumnValue: '',
        }
        const value = data.find(udField => udField.name == field.label)?.value;
        result.value = { DisplayText: value || '', Value: value || '' };
        result.modalValue = { items$: result.items$ };
        break;
    }

    return result;
  }

  mapField(
    id: string,
    field: CaseConfigField,
    data: CaseConfigData,
    userDefinedFieldsData?: UserDefineFieldData[],
    isCivil?: boolean
  ): SectionFieldModel {
    const result = <SectionFieldModel>{
      ...field,
      width: field.width === 0 ? 100 : field.width,
    };

    switch (field.id) {
      case 1:
        result.type = SectionFieldType.DoubleInputControl;
        result.label = 'File #';
        result.formControlName = 'fileNumber';
        result.value = { first: data.fileNumber.prefix, second: data.fileNumber.suffix };
        result.isDisabled = true;
        break;
      case 2:
        result.type = SectionFieldType.ButtonControl;
        result.tooltip = 'Notes(Alt+N)';
        result.modalType = ModalsTypeEnum.Notes;
        result.hasData = data.totals.caseNote > 0;
        result.modalValue = { id: id, causeNumber: data.causeNumber, isCivil: isCivil };
        result.shortcut = 'alt.n';
        break;
      case 4:
        result.type = SectionFieldType.DateInputControl;
        result.value = data.issuedDate;
        result.formControlName = 'issued';
        break;
      case 8:
        const caseCategoryLabel = this.coreService.systemValues.caseCategoryLabel || 'Category';
        result.label = caseCategoryLabel;
        result.type = SectionFieldType.DropdownControl;
        result.value = { DisplayText: data.category.tooltip || data.category.value || '', Value: data.category.value || '' };
        result.modalType = ModalsTypeEnum.Category;
        result.formControlName = 'category';
        result.tooltip = caseCategoryLabel + '(Alt + Y)';
        result.shortcut = 'alt.y';
        result.modalTitle = caseCategoryLabel;
        result.dropdownContextKey = "CASECATEGORY ,CivilFlag='true' and ExcludeFromExternal='false'";
        result.dropdownFormatterFn = commonFormatter;
        result.dropdownMethodName = "GetAutoCompleteSearchListNoJQ";
        result.dropdownValidationConfig = {
          serviceName: "Validation.asmx",
          methodName: ValidationMethod.IsExist,
          strTableName: "{{Enum220}}",
          strColumnName: "Code",
          bUseQuote: true,
          errorMessage: `Invalid ${caseCategoryLabel} Code`,
          strWhereColumnName: "Status",
          strWhereColumnValue: "N",
          bUseQuoteForWhere: true,
          inactiveErrorMessage: caseCategoryLabel + ' code is Inactive',
          strColumnNameForGetValue: RetriveCodeDescriptionUsingTilde(
            "Code",
            "Description"
          ),
        };
        break;
      case 9:
        result.type = SectionFieldType.FieldSetControl;
        result.value = {
          referral: {
            referredDate: data.referralDate,
            receivedDate: data.reportReceivedDate,
            reportNumber: data.reportNumber,
            police: { DisplayText: data.dsn.tooltip || data.dsn.value, Value: data.dsn.value },
            agency: { DisplayText: data.agency.description, Value: data.agency.value },
            ocn: data.ocn.value,
            labNumber: data.labNumber.value,
            dsnSequence: data.dsn.dsnSequence
          },
          arrest: {
            dateTime: {
              date: data.arrest.date,
              time: data.arrest.time,
            },
            number: data.arrest.number,
            hourHold: data.hourHold,
            bookingPD: { DisplayText: data.bookingPD.description, Value: data.bookingPD.value },
            neighborhood: { DisplayText: data.neighborhood.tooltip, Value: data.neighborhood.code },
            summonDate: data.summonDate,
            summonNum: data.summonNum
          },
          court: {
            issuedDate: data.issuedDate,
            courtNumber: data.causeNumber?.value,
            probOfficer: data.poBadgeNumber,
          },
        };
        result.formControlName = 'referralFieldSet';
        break;
      case 10:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.status;
        result.isDisabled = true;
        result.formControlName = 'status';
        break;
      case 11:
        result.type = SectionFieldType.DropdownControl;
        result.modalType = ModalsTypeEnum.Stage;
        result.value = { DisplayText: data.stageCodeDescription || '', Value: data.stageCode || '' };
        result.formControlName = 'stage';
        result.tooltip = 'Stage(Alt+Ctrl+D)';
        result.shortcut = 'alt.ctrl.d';
        result.dropdownContextKey = "CASESTAGE, IsCivil=1";
        result.dropdownFormatterFn = commonFormatter;
        result.dropdownMethodName = "GetAutoCompleteSearchListNoJQ";
        result.dropdownValidationConfig = {
          serviceName: "Validation.asmx",
          methodName: ValidationMethod.IsExist,
          strTableName: "{{Enum224}}",
          strColumnName: "Code",
          bUseQuote: true,
          errorMessage: `Invalid Stage`,
          strWhereColumnName: "IsCivil = 1 AND Status",
          strWhereColumnValue: "N",
          bUseQuoteForWhere: true,
          inactiveErrorMessage: "Code For Stage is Inactive",
          strColumnNameForGetValue: "Description"
        };
        break;
      case 13:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.causeNumber.value;
        result.tooltip = data.causeNumber.tooltip;
        result.formControlName = 'causeNumber';
        break;
      case 18:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.badgeNumber;
        result.formControlName = 'badgeNumber';
        break;
      case 19:
        result.type = SectionFieldType.DropdownControl;
        result.modalType = ModalsTypeEnum.Jurisdiction;
        result.value = { DisplayText: data.jurisdiction.codeTooltip || data.jurisdiction.id || '', Value: data.jurisdiction.id || '' };
        result.formControlName = 'jurisdictionId';
        result.tooltip = 'Jurisdiction(Alt + D)';
        result.shortcut = 'alt.d';
        result.dropdownContextKey = "JURISDICTION";
        result.dropdownFormatterFn = commonFormatter;
        result.dropdownMethodName = "GetAutoCompleteSearchListNoJQ";
        result.dropdownValidationConfig = {
          serviceName: "Validation.asmx",
          methodName: ValidationMethod.IsExist,
          strTableName: "{{Enum375}}",
          strColumnName: "Code",
          bUseQuote: true,
          errorMessage: `Invalid Jurisdictions Code`,
          strWhereColumnName: "Status",
          strWhereColumnValue: "N",
          bUseQuoteForWhere: true,
          inactiveErrorMessage: "Jurisdictions code is Inactive",
          strColumnNameForGetValue: "Code"
        };
        break;
      case 21:
        result.type = SectionFieldType.BorderLabelInputControl;
        result.value = data?.assigned?.name;
        result.tooltip = data?.assigned?.email ? 'Phone: ' + (data?.assigned?.phone ?? '') + '<br/>Address: ' + (data?.assigned?.address ?? '') : ''
        result.shortcut = 'alt.U';
        result.formControlName = 'assignedUser';
        result.linkData = {
          title: data?.assigned?.email || '',
          link: data?.assigned?.emailHref || ''
        };
        result.isDisabled = true;
        break;
      case 24:
        result.type = SectionFieldType.DropdownControl;
        result.modalType = ModalsTypeEnum.Attorney;

        result.value = { DisplayText: data.defense.attorneyName || data.defense.attorney || '', Value: data.defense.attorney || '' };
        result.formControlName = 'defenseAttorney';
        result.tooltip = 'Defense(Alt + K)';
        result.shortcut = 'alt.k';
        result.dropdownContextKey = "DW";
        result.dropdownFormatterFn = commonFormatter;
        result.dropdownMethodName = "SearchByAttorneyBarCodeORNameNoJQ";
        result.dropdownValidationConfig = {
          serviceName: "Validation.asmx",
          methodName: ValidationMethod.IsExist,
          strTableName: "{{Enum177}}",
          strColumnName: "BarCode",
          bUseQuote: true,
          errorMessage: `Invalid Defense Attorney`,
          strWhereColumnName: "Status",
          strWhereColumnValue: "N",
          bUseQuoteForWhere: true,
          inactiveErrorMessage: "BarCode for Defense is Inactive",
          strColumnNameForGetValue: "LastName + ' ' + FirstName"
        };

        break;
      case 26:
        result.type = SectionFieldType.ButtonControl;
        result.modalType = ModalsTypeEnum.ContactInfo;
        result.hasData = !!data.defense.attorney;
        result.modalValue = { defenseAttorney: data.defense.attorney };
        break;
      case 27:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.weight.value ? data.weight.value : '';
        result.tooltip = data.weight.tooltip;
        result.formControlName = 'weight';
        break;
      case 28:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.civil.matter;
        result.formControlName = 'civilMatter';
        break;
      case 29:
        result.type = SectionFieldType.TextareaControl;
        result.value = data.civil.description;
        result.formControlName = 'civilDescription';
        break;
      case 30:
        result.type = SectionFieldType.DropdownControl;
        result.modalType = ModalsTypeEnum.Client;
        result.value = { DisplayText: data.civil.clientDescription || data.civil.client || '', Value: data.civil.client || '' };
        result.formControlName = 'civilClient';
        result.tooltip = 'Client(Alt+Ctrl+C)';
        result.shortcut = 'alt.ctrl.c';
        result.dropdownContextKey = "CIVILCLIENT";
        result.dropdownFormatterFn = commonFormatter;
        result.dropdownMethodName = "GetAutoCompleteSearchListNoJQ";
        result.dropdownValidationConfig = {
          serviceName: "Validation.asmx",
          methodName: ValidationMethod.IsExist,
          strTableName: "tblCtCivilClient",
          strColumnName: "Code",
          bUseQuote: true,
          errorMessage: `Invalid CivilClient`,
          strWhereColumnName: "Inactive",
          strWhereColumnValue: "false",
          bUseQuoteForWhere: true,
          inactiveErrorMessage: "Inactive CivilClient",
          strColumnNameForGetValue: "Description"
        };
        break;
      case 31:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.civil.work;
        result.formControlName = 'civilWork';
        break;
      case 32:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.civil.area;
        result.formControlName = 'civilArea';
        break;
      case 33:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.civil.funded;
        result.formControlName = 'civilFunded';
        break;
      case 34:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.civil.cats;
        result.formControlName = 'civilCats';
        break;
      case 35:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.civil.type;
        result.formControlName = 'civilTypes';
        break;
      case 36:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.civil.number;
        result.formControlName = 'civilNumber';
        break;
      case 37:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.civil.disposition;
        result.formControlName = 'civilDisposition';
        break;
      case 38:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.civil.explanation;
        result.formControlName = 'civilExplanation';
        break;
      case 41:
        result.type = SectionFieldType.RightCheckboxControl;
        result.value = data.proSe;
        result.formControlName = 'proSe';
        break;
      case 42:
        result.type = SectionFieldType.RightCheckboxControl;
        result.value = data.defense.pdRetainer;
        result.formControlName = 'defensePdRetainer';
        break;
      case 43:
        result.type = SectionFieldType.ButtonControl;
        result.tooltip = 'Co-Counsel(Alt+8)';
        result.modalType = ModalsTypeEnum.CoCounsel;
        result.hasData = data.totals.defCoCounsel > 0;
        result.modalValue = { id: id };
        result.shortcut = 'alt.8';
        break;
      case 1079:
        result.type = SectionFieldType.DateInputControl;
        result.value = data.referralDate;
        result.formControlName = 'referralDate';
        break;
      case 1080:
        result.type = SectionFieldType.DateInputControl;
        result.value = data.reportReceivedDate;
        result.formControlName = 'reportReceivedDate';
        break;
      case 1081:
        result.type = SectionFieldType.ClickableLabelInputControl;
        result.value = data.reportNo.value;
        result.tooltip = data.reportNo.tooltip ?? 'Report #(ALT+R)';
        result.modalType = ModalsTypeEnum.ReportNo;
        result.hasData = data.totals.reportNumbers > 0;
        console.log("Report Numer id ::", id);

        result.modalValue = { id: id };
        result.shortcut = 'alt.r';
        result.formControlName = 'reportNo';
        result.textTransform = 'uppercase';
        break;
      case 1082:
        result.type = SectionFieldType.DropdownControl;
        result.modalType = ModalsTypeEnum.Police;
        result.value = { DisplayText: data.dsn.tooltip || data.dsn.value || '', Value: data.dsn.value || '' };
        result.formControlName = 'dsn';
        result.tooltip = 'Police(Alt + 7)';
        result.shortcut = 'alt.7';
        result.dropdownContextKey = "FILELOCATION";
        result.dropdownMethodName = "SearchByDSNNoJQ";
        result.dropdownValidationConfig = {
          serviceName: "Validation.asmx",
          methodName: ValidationMethod.IsExist,
          strTableName: "{{Enum302}}",
          strColumnName: "DSNSequence",
          bUseQuote: true,
          errorMessage: `Invalid DSN Number`,
          strWhereColumnName: "convert(varchar, ISNULL(TerminationDate,Space(1)) ,111)",
          strWhereColumnValue: "1900/01/01",
          bUseQuoteForWhere: true,
          inactiveErrorMessage: "Invalid DSN",
        };
        result.dropdownSupressValidation = true;
        result.updateOn = 'blur';
        break;
      case 1083:
        result.type = SectionFieldType.DropdownControl;
        result.modalType = ModalsTypeEnum.PoliceDepartments;
        result.value = { DisplayText: data.agency.description || '', Value: data.agency.value || '' };
        result.formControlName = 'agency';
        result.tooltip = 'Agency';
        result.dropdownContextKey = "";
        result.dropdownFormatterFn = commonFormatter;
        result.dropdownMethodName = "SearchByPdNumberNoJQ";
        result.dropdownValidationConfig = {
          serviceName: "Validation.asmx",
          methodName: ValidationMethod.IsExist,
          strTableName: "tblCtPoliceDepartments",
          strColumnName: "PdNumber",
          bUseQuote: true,
          errorMessage: `Invalid Police Department`,
          strWhereColumnName: "ISNULL(Status,'N')",
          strWhereColumnValue: "'N'",
          bUseQuoteForWhere: false,
          inactiveErrorMessage: "Code Is Inactive",
          strColumnNameForGetValue: "PdName"
        };
        break;
      case 1084:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.ocn.value;
        result.tooltip = data.ocn.tooltip;
        result.formControlName = 'ocn';
        break;
      case 1085:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.labNumber.value;
        result.formControlName = 'labNumber';
        break;
      case 1086:
        result.type = SectionFieldType.TextareaControl;
        result.value = data.civil.description;
        result.formControlName = 'civilDescription';
        break;
      case 1087:
        result.type = SectionFieldType.DateAtTime;
        result.value = {
          date: data.arrest.date,
          time: data.arrest.time,
        };
        result.formControlName = 'dateTime';
        result.errorMessage =
          'Arrest date and time cannot be greater than the current date and time';
        break;
      case 1088:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.arrest.number;
        result.formControlName = 'arrestNumber';
        break;
      case 1089:
        result.type = SectionFieldType.HourHold;
        result.value = data.hourHold;
        result.formControlName = 'hourHold';
        break;
      case 1090:
        result.type = SectionFieldType.DropdownControl;
        result.modalType = ModalsTypeEnum.PoliceDepartments;
        result.value = { DisplayText: data.bookingPD.description || '', Value: data.bookingPD.value || '' };
        result.formControlName = 'bookingPd';
        result.dropdownContextKey = "POLICEDEPT";
        result.dropdownFormatterFn = commonFormatter;
        result.dropdownMethodName = "GetAutoCompleteSearchListNoJQ";
        result.dropdownValidationConfig = {
          serviceName: "Validation.asmx",
          methodName: ValidationMethod.IsExist,
          strTableName: "{{Enum406}}",
          strColumnName: "PdNumber",
          bUseQuote: true,
          errorMessage: `Invalid Police Department`,
          strWhereColumnName: "ISNULL(Status,'N')",
          strWhereColumnValue: "N",
          bUseQuoteForWhere: true,
          inactiveErrorMessage: "Code Is Inactive",
          strColumnNameForGetValue: RetriveDescription('PdNumber', 'PdName')
        };
        result.tooltip = 'Booking PD(Alt+9)';
        result.shortcut = 'alt.9';
        break;
      case 1091:
        result.type = SectionFieldType.LinkControl;
        break;
      case 1092:
        result.type = SectionFieldType.DropdownControl;
        result.modalType = ModalsTypeEnum.Neighborhood;
        result.value = { DisplayText: data.neighborhood.tooltip || '', Value: data.neighborhood.code || '' };
        result.formControlName = 'neighborhood';
        result.tooltip = 'Neighborhood(Alt+Ctrl+G)';
        result.shortcut = 'alt.ctrl.g';
        result.dropdownContextKey = "NEIGHBORHOOD";
        result.dropdownFormatterFn = commonFormatter;
        result.dropdownMethodName = "GetAutoCompleteSearchListNoJQ";
        result.dropdownValidationConfig = {
          serviceName: "Validation.asmx",
          methodName: ValidationMethod.IsExist,
          strTableName: "{{Enum391}}",
          strColumnName: "Code",
          bUseQuote: true,
          errorMessage: `Invalid Neighborhood`,
          strWhereColumnName: "inactive",
          strWhereColumnValue: "false",
          bUseQuoteForWhere: true,
          inactiveErrorMessage: "Inactive Neighborhood",
          strColumnNameForGetValue: "Description"
        };
        break;
      case 1093:
        result.type = SectionFieldType.ButtonControl;
        break;
      case 1094:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.ticketNumber;
        result.formControlName = 'ticketNumber';
        console.log("result ::", result.value, data.ticketNumber, result.label);
        
        break;
      case 1095:
        result.type = SectionFieldType.CheckboxInputControl;
        result.value = { isChecked: data.accident.flag === 'Y', text: data.accident.number };
        result.formControlName = 'accidentNumber';
        break;
      case 1096:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.clockedSpeed;
        result.formControlName = 'clockedSpeed';
        result.textAlign = 'right';
        break;
      case 1097:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.postedSpeed;
        result.formControlName = 'postedSpeed';
        result.textAlign = 'right';
        break;
      case 1098:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.mphOver;
        result.formControlName = 'mphOver';
        break;
      case 1099:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.vehicle.make;
        result.formControlName = 'vehicleMake';
        break;
      case 1100:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.vehicle.model;
        result.formControlName = 'vehicleModel';
        break;
      case 1101:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.vehicle.year;
        result.formControlName = 'vehicleYear';
        break;
      case 1102:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.vehicle.vin;
        result.formControlName = 'vehicleVin';
        break;
      case 1103:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.vehicle.color;
        result.formControlName = 'vehicleColor';
        break;
      case 1104:
        result.type = SectionFieldType.DoubleInputControl;
        result.value = { first: data.vehicle.state, second: data.vehicle.license };
        result.formControlName = 'license';
        break;
      case 1105:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.vehicle.licenseExpirationYear;
        result.formControlName = 'vehicleLicenseExpirationYear';
        break;
      case 1106:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.vehicle.registrationWeight ? data.vehicle.registrationWeight : '';
        result.formControlName = 'registrationWeight';
        break;
      case 1107:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.vehicle.actualWeight ? data.vehicle.actualWeight : '';
        result.formControlName = 'actualWeight';
        break;
      case 1108:
        result.type = SectionFieldType.LeftCheckboxControl;
        result.value = data.isFatality;
        result.formControlName = 'isFatality';
        break;
      case 1109:
        result.type = SectionFieldType.LeftCheckboxControl;
        result.value = data.isLicenseSurrender;
        result.formControlName = 'isLicenseSurrender';
        break;
      case 1110:
        result.type = SectionFieldType.LeftCheckboxControl;
        result.value = data.isIgnitionInterlock;
        result.formControlName = 'isIgnitionInterlock';
        break;
      case 1111:
        result.type = SectionFieldType.LeftCheckboxControl;
        result.value = data.isConstructionZone;
        result.formControlName = 'isConstructionZone';
        break;
      case 1112:
        result.type = SectionFieldType.LeftCheckboxControl;
        result.value = data.isSignWork;
        result.formControlName = 'isSignWork';
        break;
      case 1113:
        result.type = SectionFieldType.LeftCheckboxControl;
        result.value = data.isTravelSafeZone;
        result.formControlName = 'isTravelSafeZone';
        break;
      case 1115:
        result.type = SectionFieldType.LeftCheckboxControl;
        result.value = data.isDWI;
        result.formControlName = 'isDwi';
        break;
      case 1116:
        result.type = SectionFieldType.LeftCheckboxControl;
        result.value = data.isVehicleOperation;
        result.formControlName = 'isVehicleOperation';
        break;
      case 1117:
        result.type = SectionFieldType.LeftCheckboxControl;
        result.value = data.isCommVeh;
        result.formControlName = 'isCommVeh';
        break;
      case 1118:
        console.log("Label for few ::", result.label);

        result.type = SectionFieldType.DropdownControl;
        result.value = { DisplayText: data.dwiTest.tooltip || '', Value: data.dwiTest.agency || '' };
        result.modalType = ModalsTypeEnum.PoliceDepartments;
        result.tooltip = 'L. E. Agency (ALT+G)';
        result.shortcut = 'alt.g';
        result.formControlName = 'dwiTestAgency';
        result.dropdownContextKey = "";
        result.dropdownFormatterFn = commonTildFormatter;
        result.dropdownMethodName = "SearchByPdNumberNoJQUsingTild";
        result.dropdownValidationConfig = {
          serviceName: "Validation.asmx",
          methodName: ValidationMethod.IsExist,
          strTableName: "{{Enum406}}",
          strColumnName: "PdNumber",
          bUseQuote: true,
          errorMessage: `Invalid PD Number`,
          strWhereColumnName: "ISNULL(Status,'N')",
          strWhereColumnValue: "N",
          bUseQuoteForWhere: true,
          inactiveErrorMessage: "Code Is Inactive",
          strColumnNameForGetValue: "PdName"
        };
        break;
      case 1119:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.dwiTest.type;
        result.formControlName = 'dwiTestType';
        break;
      case 1120:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.bloodAlcoholContent;
        result.formControlName = 'bloodAlcoholContent';
        break;
      case 1121:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.instrumentNo;
        result.formControlName = 'instrumentNo';
        break;
      case 1122:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.simulatorNumber;
        result.formControlName = 'simulatorNumber';
        break;
      case 1123:
        result.type = SectionFieldType.LabelInputControl;
        result.value = data.certificationDate;
        result.formControlName = 'certificationDate';
        break;
      default:
        result.type = SectionFieldType.LabelInputControl;
        result.value = userDefinedFieldsData?.find(f => f.name === field.label)?.value ?? '';
        result.formControlName = `${field.label}-${field.id}`;
        break;
    }

    return result;
  }

  mapTab(tab: CaseConfigTab): CaseConfigTabModel | null {
    switch (tab.identifier) {
      case 'CHARGE':
        return { name: tab.name ?? 'Charge', type: CivilCaseTabType.Charge, hasData: tab.hasData };
      case 'DOCUMENT':
        return {
          name: tab.name ?? 'Documents',
          type: CivilCaseTabType.Document,
          hasData: tab.hasData,
        };
      case 'CODEFENDANT':
        return {
          name: tab.name ?? 'Co-Defend',
          type: CivilCaseTabType.CoDefendant,
          hasData: tab.hasData,
        };
      case 'COURTDATE':
        return {
          name: tab.name ?? 'Court Dates',
          type: CivilCaseTabType.CourtDate,
        };
      case 'BOND':
        return { name: tab.name ?? 'Bond', type: CivilCaseTabType.Bond };
      case 'WITNESS':
        return { name: tab.name ?? 'Party', type: CivilCaseTabType.Party, hasData: tab.hasData };
      default:
        return null;
    }
  }
}
